import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import Text from 'UIKit/Text/Text'
import { BonusStatusEnums, BonusStep } from 'config/types/bonus'
import {
  BoosterWagerBonus as BoosterWagerBonusType,
  UserBonus,
  WageringConditionBonus,
} from 'config/types/bonus/userBonus'
import { useCountDown } from 'hooks/useCountdown'
import React, { useCallback, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { buildPluralizeText } from 'utils'
import { isCashbackBonusType } from 'utils/bonus'
import { ValidatedBonusStatusEnums, useValidateBonus } from 'views/BonusCenter/hooks'
import BonusAction from '../../../BonusAction'
import UpcomingBonus from '../../../UpcomingBonus'
import BoosterWagerBonusCondition from '../BonusContentCondition/BoostWagerBonusCondition'
import WageringBonusCondition from '../BonusContentCondition/DepositBonusCondition'
import BonusContentExpiryDate from '../Common/BonusContentExpiryDate'
import BonusItemName from '../Common/BonusItemName'
import { BonusContentFieldTitle, BonusContentGridSystem } from '../styled'

interface BonusItemProps {
  bonus: UserBonus
  onBonusSubmitted: (useSuccessfully: boolean) => void
}

const AvailableBonusContent: React.FC<BonusItemProps> = ({ bonus, onBonusSubmitted }) => {
  const validatedResult: ValidatedBonusStatusEnums = useValidateBonus(bonus)
  const [isExpired, setIsExpired] = useState(false)

  const { t } = useTranslation()
  useCountDown(
    bonus.condition.expiryDate,
    () => '',
    () => {
      setIsExpired(true)
    },
  )

  const handleSubmitBonus = useCallback(
    (isSuccedd: boolean) => {
      onBonusSubmitted(isSuccedd)
    },
    [bonus],
  )

  return (
    <>
      <BonusItemName bonus={bonus} mb="8px" />

      {bonus instanceof WageringConditionBonus && <WageringBonusCondition bonus={bonus} mt="8px" />}
      {bonus instanceof BoosterWagerBonusType && <BoosterWagerBonusCondition bonus={bonus} mt="8px" />}
      {isCashbackBonusType(bonus) && (
        <BonusContentGridSystem mt="8px">
          <BonusContentFieldTitle>
            <Trans>Duration</Trans>
          </BonusContentFieldTitle>
          <Text fontWeight="600" fontSize="12px">
            {t(
              buildPluralizeText('{{duration}} {{hour}}', [
                {
                  number: bonus.cashbackActiveDurationInHours,
                  key: 'hour',
                  word: ['hour', 'hours'],
                },
              ]),
              { duration: bonus.cashbackActiveDurationInHours },
            )}
          </Text>
        </BonusContentGridSystem>
      )}
      <BonusContentExpiryDate bonus={bonus} mt="8px" />

      {isCashbackBonusType(bonus) && bonus.status === BonusStatusEnums.Available && !bonus.condition.isExpired && (
        <Text
          fontSize="10px"
          color="textSubtle"
          fontWeight={500}
          letterSpacing={-0.24}
          mt="12px"
          style={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}
          maxWidth={332}
        >
          ⚠️ <Trans>Activating this bonus will disable regular cashback for any affected periods</Trans>.
        </Text>
      )}

      {bonus instanceof WageringConditionBonus &&
        bonus.isCreaditReward &&
        (!bonus.CurrentStepInfo ||
          (bonus.CurrentStepInfo &&
            (bonus.CurrentStepInfo.stepContent === BonusStep.Active ||
              bonus.CurrentStepInfo.stepContent === BonusStep.Claim))) && (
          <Text fontSize="10px" color="textSubtle" textAlign="center" mt="12px">
            {t('Bonus will be added to your real balance upon activation')}
          </Text>
        )}

      <Box mt="16px">
        <Flex width="100%">
          {validatedResult === ValidatedBonusStatusEnums.Available && (
            <StyledButton
              trackingModule="avaiableBonusItem"
              onBonusSubmitted={handleSubmitBonus}
              bonus={bonus}
              disabled={isExpired}
            />
          )}
        </Flex>

        {validatedResult === ValidatedBonusStatusEnums.Available && (
          <>{bonus.bonusUpcoming?.length > 0 && <UpcomingBonus mt="20px" bonus={bonus} />}</>
        )}
      </Box>
    </>
  )
}

const StyledButton = styled(BonusAction)`
  height: 40px;
  background: linear-gradient(180deg, #ff85cb 0%, #e960af 100%);
  border-radius: 8px;
  white-space: nowrap;
  box-sizing: border-box;
  padding: 0px !important;
  flex: 1 1;
  font-size: 14px;
  font-weight: bold;
`

export default AvailableBonusContent
